<template>
  <div id="permissionModal" ref="modalRef" tabindex="-1" aria-hidden="true" class="modal hidden">
    <div v-if="modalType" class="modal-body w-[500px]">
      <CloseButton @close="closeModal()"></CloseButton>
      <div class="modal-body__content text-center flex flex-col gap-4">
        <img :src="'/images/emojis/' + modalEmoji" alt="modal emoji" class="w-14 mx-auto mt-4" />
        <h3 class="text-xl font-semibold text-gray-900 mt-6">
          {{ modalTitle }}
        </h3>
        <p class="text-base font-normal text-gray-500">
          {{ modalSubtitle }}
        </p>

        <div
          v-if="modalType === PermissionModalType.ERROR"
          class="text-left flex p-4 text-sm text-gray-900 rounded-lg bg-gray-50"
          role="alert"
        >
          <svg
            class="shrink-0 inline w-4 h-4 mr-3 mt-[2px]"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
            />
          </svg>
          <span class="sr-only">Warning</span>
          <div>
            <span class="font-medium mb-3">
              <span v-if="modalType === PermissionModalType.ERROR"
                >Why are permissions needed?
              </span>
            </span>
            <p class="text-gray-500">
              <span v-if="modalType === PermissionModalType.ERROR">
                We need your permission to read and modify your Strava activities to calculate and
                add snacks based on burned calories. Rest assured, we don't collect any personal
                data. Feel free to check out our
                <a @click="closeModal()" class="underline" href="#faqs">FAQs</a> for more
                information.
              </span>
            </p>
          </div>
        </div>
        <div>
          <button @click="closeModal()" type="button" class="mt-5 button solid-button">
            {{ modalCTA }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CloseButton from '@/components/CloseButton.vue'
import { defaultModalOptions } from '@/constants/modal-options'
import { ModalType } from '@/enums/modal-type'
import { PermissionModalType } from '@/enums/permission-modal-type'
import { ProvidedStravaScope } from '@/enums/provided-strava-scope'
import router from '@/router'
import EdgeFunctionsWrapperService from '@/services/edge-functions-wrapper'
import LoadingIndicatorHelper from '@/services/loading-indicator-helper'
import ModalHelperService from '@/services/modal-helper'
import snackbarHelper from '@/services/snackbar-helper'
import StravaHelperService from '@/services/strava-helper'
import UserService from '@/services/user'
import { type ModalOptions } from 'flowbite'
import { computed, onMounted, ref } from 'vue'

const modalRef = ref<HTMLDivElement>()
const modalOptions: ModalOptions = {
  ...defaultModalOptions,

  onHide: () => {
    clearQueryParameters()
  }
}

const modalType = ref<PermissionModalType>()

onMounted(() => {
  initModal()
  checkUrlSearchParams()
})

function initModal(): void {
  ModalHelperService.init(ModalType.PERMISSION, modalRef.value!, modalOptions)
}

function closeModal(): void {
  ModalHelperService.hide(ModalType.PERMISSION)
}

function showModal(): void {
  ModalHelperService.show(ModalType.PERMISSION)
}

function clearQueryParameters(): void {
  var currentURL = window.location.href
  window.history.replaceState({}, document.title, currentURL.split('?')[0])
}

function checkUrlSearchParams() {
  const currentUrl = window.location.href
  const urlParams = new URLSearchParams(currentUrl)

  if (urlParams.get('error') === 'access_denied') {
    onAccessDenied()
    return
  }

  const scope = urlParams.get('scope')
  const code = urlParams.get('code')

  if (scope && code) {
    onCheckScope(scope, code)
  }
}

function onAccessDenied() {
  modalType.value = PermissionModalType.ERROR
  showModal()
}

async function onCheckScope(scope: string, code: string): Promise<void> {
  const providedScope = StravaHelperService.checkProvidedScope(scope)
  mapScopeToModalType(providedScope)

  if (providedScope === ProvidedStravaScope.INSUFFICIENT) {
    showModal()
    return
  }

  try {
    LoadingIndicatorHelper.showLoading()
    const user = await EdgeFunctionsWrapperService.exchangeStravaToken(code, scope)
    UserService.setUser(user)
    router.push({ name: 'profile' }).then(() => showModal())
  } catch (e) {
    console.error('error', e)
    closeModal()
    LoadingIndicatorHelper.hideLoading()
    snackbarHelper.show('Error connecting to Fitsnack. Please try again.', true)
  }
}

function mapScopeToModalType(scope: ProvidedStravaScope): void {
  switch (scope) {
    case ProvidedStravaScope.INSUFFICIENT:
      modalType.value = PermissionModalType.ERROR
      break
    case ProvidedStravaScope.MAX:
      modalType.value = PermissionModalType.SUCCESS
      break
  }
}

const modalEmoji = computed(() => {
  switch (modalType.value) {
    case PermissionModalType.ERROR:
      return 'crying-face.svg'
    case PermissionModalType.SUCCESS:
      return 'wave.svg'
    default:
      return ''
  }
})

const modalTitle = computed(() => {
  switch (modalType.value) {
    case PermissionModalType.ERROR:
      return 'Oops, Permissions Missing'
    case PermissionModalType.SUCCESS:
      return 'Welcome to Fitsnack!'
    default:
      return ''
  }
})

const modalSubtitle = computed(() => {
  switch (modalType.value) {
    case PermissionModalType.ERROR:
      return 'We need your permission to add snacks to your Strava activity description.'
    case PermissionModalType.SUCCESS:
      return "You're connected! Now pick the snacks you want to burn with your activities."
    default:
      return ''
  }
})

const modalCTA = computed(() => {
  switch (modalType.value) {
    case PermissionModalType.ERROR:
      return 'Close'
    case PermissionModalType.SUCCESS:
      return 'Get started'
    default:
      return ''
  }
})
</script>

<style scoped></style>
